import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import styled from 'styled-components'
import colors from '../../../configuration/colors'
import { hideModal } from '../../../actions/modal'
import Modal from '../../mini/Modal'
import TextInputComponent from './InputComponents/TextInputComponent'
import Button from '../../mini/Buttons/Button'
import CancelButton from '../../mini/Buttons/CancelButton'
import H2 from '../../typography/headers/H2'
import MarginRight15 from '../../typography/margins/MarginRight15'
import {
  editActivityCourseJourney,
  fetchActivityCourseJourney
} from '../../mycourses/Journeys/API'
import withApiData from '../../HOC/withApiData'
import EditCourseIcon from '../../../static/images/mycourses/icon_edit_course.svg'
import { italicsAndBoldOnlyMDEOptions } from '../../../utils/markdowneditor'

const ModalContainer = styled.div`
  width: 660px;
  margin: 100px auto;
  border-radius: 10px;
  box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.08);
  background-color: ${colors.smoke};
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 10px 30px 30px;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const EditJourneyModal = ({ apiData, activityCourseId, journeyId }) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [isMutating, setIsMutating] = useState(false)

  const journey = useQuery(
    ['activity-course-journey-' + journeyId],
    () =>
      fetchActivityCourseJourney(
        apiData.orgId,
        apiData.token,
        activityCourseId,
        journeyId
      ),
    {
      refetchOnWindowFocus: false
    }
  )

  const [journeyName, setJourneyName] = useState('')
  const [journeyDescription, setJourneyDescription] = useState('')

  useEffect(() => {
    if (!journey.isLoading) {
      setJourneyName(journey.data.name)
      setJourneyDescription(journey.data.description)
    }
  }, [journey.isLoading])

  const handleJourneyName = e => {
    setJourneyName(e.target.value)
  }

  const handleJourneyDescription = e => {
    let event = {
      target: {}
    }
    event.target.id = 'journey-description'
    event.target.value = e

    setJourneyDescription(event.target.value)
  }

  const couldSaveData = journeyName.length > 0

  const handleCloseModal = () => {
    dispatch(hideModal())
  }

  const mutation = useMutation(({ journeyName, journeyDescription }) =>
    editActivityCourseJourney(
      apiData,
      activityCourseId,
      journeyId,
      journeyName,
      journeyDescription
    )
  )

  const handleSaveEditedCourse = () => {
    if (couldSaveData) {
      setIsMutating(true)
      mutation.mutate(
        {
          journeyName,
          journeyDescription
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(
              'activity-course-journeys-' + activityCourseId
            )
            queryClient.invalidateQueries(
              'activity-course-journey-' + journeyId
            )
            setTimeout(() => {
              setIsMutating(false)
              handleCloseModal()
            }, 300)
          }
        }
      )
    }
  }

  const journeyDescriptionOptions = Object.assign(
    {},
    italicsAndBoldOnlyMDEOptions
  )
  journeyDescriptionOptions.initialValue = journeyDescription
  journeyDescriptionOptions.placeholder = 'Journey Description'
  journeyDescriptionOptions.autofocus = false

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        <HeaderContainer>
          <H2>
            <span style={{ marginRight: '12px' }}>
              <img src={EditCourseIcon} />
            </span>
            Edit Journey
          </H2>
          <FlexContainer>
            <MarginRight15>
              <CancelButton
                handleClick={handleCloseModal}
                title="Cancel"
                width="104px"
              />
            </MarginRight15>
            <div
              data-for="editCourseTooltip"
              data-tip="Please enter all required fields prior to update this Journey."
            >
              <Button
                width="104px"
                title="Save"
                handleClick={handleSaveEditedCourse}
                buttonColor={colors.oeYellow}
                borderColor={colors.oeYellow}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
                isMutating={isMutating}
                couldSaveData={couldSaveData}
                shouldShowTooltipAndDisableButton={true}
              />
              {!couldSaveData && (
                <ReactTooltip
                  id="editCourseTooltip"
                  place="bottom"
                  effect="solid"
                  className="activityCourseTooltip"
                />
              )}
            </div>
          </FlexContainer>
        </HeaderContainer>
        <MainContainer>
          <TextInputComponent
            title="Journey  Name "
            handleEnteredTextValue={handleJourneyName}
            value={journeyName}
            shouldDisplayAsteriskSymbol={true}
          />
          <TextInputComponent
            title="Journey Description"
            placeholder="A basic overview (2-5 sentences) of the journey, including its overarching theme and intended outcomes (both skill and content). "
            handleEnteredTextValue={handleJourneyDescription}
            hintText="A basic overview (2-5 sentences) of the journey, including its overarching theme and intended outcomes (both skill and content)."
            value={journeyDescription}
            shouldDisplayAsteriskSymbol={false}
            shouldBeTextarea={true}
            options={journeyDescriptionOptions}
          />
        </MainContainer>
      </ModalContainer>
    </Modal>
  )
}

EditJourneyModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourseId: PropTypes.number.isRequired,
  journeyId: PropTypes.number.isRequired
}

export default withApiData(EditJourneyModal)



// WEBPACK FOOTER //
// ./src/components/modal/CreateOrEditCourseModal/EditJourneyModal.js